/* Section tabs — IIFE-scoped controller for the nested-blocks model.
   See spec at docs/superpowers/specs/2026-05-10-section-tabs-nested-blocks-design.md.

   Architecture note: theme block settings (block.settings.X) are NOT
   accessible from the parent section's iteration scope. So each Tab theme
   block emits its OWN <button role="tab"> + <div role="tabpanel"> pair
   using its own settings. On hydration, this script moves those buttons
   into the section's empty <div role="tablist"> placeholder. Panels stay
   in place; we toggle their `hidden` attribute by index.

   We intentionally avoid customElements.define here per the project's
   feedback note on custom-element upgrade timing in this theme. */

(function () {
  var INITIALIZED = 'sectionTabsInitialized';
  var instances = [];

  function SectionTabs(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId || '';
    this.tablist = root.querySelector(':scope > [data-section-tabs-list]') ||
                   root.querySelector(':scope > .section-tabs__list');
    this.hydrate();

    var defaultIndex = parseInt(root.dataset.default || '0', 10);
    if (isNaN(defaultIndex) || defaultIndex < 0) defaultIndex = 0;
    if (this.tabs.length > 0 && defaultIndex >= this.tabs.length) defaultIndex = 0;

    this.activate(defaultIndex);
    root.classList.add('section-tabs--ready');

    this._onClick = this.onClick.bind(this);
    this._onKeydown = this.onKeydown.bind(this);
    root.addEventListener('click', this._onClick);
    root.addEventListener('keydown', this._onKeydown);
  }

  /* Move the per-block tab buttons into the section's tablist (preserving
     document order), then wire id / aria-controls / aria-labelledby
     relationships from this section's id. */
  SectionTabs.prototype.hydrate = function () {
    if (!this.tablist) {
      this.tabs = [];
      this.panels = [];
      return;
    }
    var sectionId = this.sectionId;
    var buttons = Array.prototype.slice.call(
      this.root.querySelectorAll(':scope > [data-section-tabs-tab]')
    );
    var panels = Array.prototype.slice.call(
      this.root.querySelectorAll(':scope > [data-section-tabs-panel]')
    );
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      btn.id = 'st-' + sectionId + '-' + i;
      btn.setAttribute('data-tab-index', i);
      btn.setAttribute('aria-controls', 'st-' + sectionId + '-panel-' + i);
      this.tablist.appendChild(btn);
    }
    for (var p = 0; p < panels.length; p++) {
      var panel = panels[p];
      panel.id = 'st-' + sectionId + '-panel-' + p;
      panel.setAttribute('aria-labelledby', 'st-' + sectionId + '-' + p);
    }
    this.tabs = buttons;
    this.panels = panels;
  };

  SectionTabs.prototype.activate = function (index) {
    if (index < 0 || index >= this.tabs.length) return;
    var designMode = window.Shopify && window.Shopify.designMode === true;
    this.tabs.forEach(function (tab, i) {
      var selected = i === index;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.setAttribute('tabindex', selected ? '0' : '-1');
    });
    this.panels.forEach(function (panel, i) {
      panel.hidden = !designMode && i !== index;
    });
    this.activeIndex = index;
    this.scrollActiveIntoView(index);
  };

  SectionTabs.prototype.scrollActiveIntoView = function (index) {
    var tab = this.tabs[index];
    if (!tab || typeof tab.scrollIntoView !== 'function') return;
    if (window.Shopify && window.Shopify.designMode === true) return;
    if (!this.root.classList.contains('section-tabs--mobile-scroll')) return;
    try {
      tab.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
    } catch (e) {
      tab.scrollIntoView();
    }
  };

  SectionTabs.prototype.onClick = function (event) {
    var tab = event.target.closest('[role="tab"]');
    if (!tab || !this.root.contains(tab)) return;
    var idx = parseInt(tab.dataset.tabIndex, 10);
    if (isNaN(idx)) return;
    this.activate(idx);
  };

  SectionTabs.prototype.onKeydown = function (event) {
    var tab = event.target.closest('[role="tab"]');
    if (!tab || !this.root.contains(tab)) return;
    var current = this.tabs.indexOf(tab);
    if (current < 0) return;
    var last = this.tabs.length - 1;
    var next = current;
    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        next = current === 0 ? last : current - 1;
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        next = current === last ? 0 : current + 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      case 'Enter':
      case ' ':
      case 'Spacebar':
        event.preventDefault();
        return;
      default:
        return;
    }
    event.preventDefault();
    this.activate(next);
    var nextTab = this.tabs[next];
    if (nextTab) nextTab.focus();
  };

  SectionTabs.prototype.rebind = function () {
    this.hydrate();
    var idx = typeof this.activeIndex === 'number' ? this.activeIndex : 0;
    if (this.tabs.length === 0) {
      this.activeIndex = 0;
      return;
    }
    if (idx >= this.tabs.length) idx = 0;
    this.activate(idx);
  };

  SectionTabs.prototype.handleBlockSelect = function (event) {
    if (!event.detail || !event.target) return;
    var blockId = event.detail.blockId;
    if (!blockId) return;
    var directTab = this.root.querySelector(
      '[role="tab"][data-shopify-editor-block-handle="' + blockId + '"], ' +
      '[role="tab"][data-block-id="' + blockId + '"]'
    );
    if (directTab) {
      var idx = parseInt(directTab.dataset.tabIndex, 10);
      if (!isNaN(idx)) this.activate(idx);
      return;
    }
    for (var i = 0; i < this.panels.length; i++) {
      if (this.panels[i].querySelector('[data-shopify-editor-block-handle="' + blockId + '"], [data-block-id="' + blockId + '"]')) {
        this.activate(i);
        return;
      }
    }
  };

  function initAll() {
    var roots = document.querySelectorAll('.section-tabs');
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      if (root.dataset[INITIALIZED]) continue;
      root.dataset[INITIALIZED] = '1';
      try {
        instances.push(new SectionTabs(root));
      } catch (e) {
        if (window.console && console.error) console.error('[section-tabs] init failed', e);
      }
    }
  }

  function rebindAll() {
    instances = instances.filter(function (instance) {
      return document.body.contains(instance.root);
    });
    instances.forEach(function (instance) {
      try { instance.rebind(); } catch (e) {
        if (window.console && console.error) console.error('[section-tabs] rebind failed', e);
      }
    });
  }

  function dispatchToInstances(event) {
    instances.forEach(function (instance) {
      try { instance.handleBlockSelect(event); } catch (e) {
        if (window.console && console.error) console.error('[section-tabs] handler failed', e);
      }
    });
  }

  document.addEventListener('shopify:section:load',    function () { initAll(); rebindAll(); });
  document.addEventListener('shopify:section:reorder', function () { rebindAll(); });
  document.addEventListener('shopify:section:unload',  function () { rebindAll(); });
  document.addEventListener('shopify:block:select',    function (e) { dispatchToInstances(e); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
